/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import { formatNameFirstLast, formatNameLastFirst } from '@strata/helper-functions';

import { NDP_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

const mapOutboundFile = ({
    file_id: fileId,
    pharmacy_name: pharmacy,
    payer_name: payer,
    script_count: scriptCount,
    prescription_count: prescriptionCount,
    file_name: fileName,
    created_date: createdDate,
    reviewed,
}) => ({
    id: fileId,
    pharmacy,
    payer,
    prescriptions: scriptCount ?? prescriptionCount,
    fileName,
    createdDate,
    reviewed: !!reviewed,
});

const mapOutboundPlanGroup = ({
    outbound_file_id: outboundFileId,
    pharmacy_name: pharmacy,
    payer_name: payer,
    script_count: scriptCount,
    file_name: fileName,
    npi,
    pharmacist_in_charge: pharmacistInCharge,
    pharmacy_address: pharmacyAddress,
    receiving_pharmacy_address: receivingPharmacyAddress,
    pharmacy_email: pharmacyEmail,
}) => ({
    id: outboundFileId,
    outboundFileId,
    pharmacy,
    payer,
    prescriptions: scriptCount,
    fileName,
    reviewed: false,
    npi,
    pharmacistInCharge,
    pharmacyAddress: pharmacyAddress ?? receivingPharmacyAddress,
    pharmacyEmail,
});

const mapOutboundFileScript = ({
    rx_number: rxNumber,
    patient_first_name: patientFirstName,
    patient_last_name: patientLastName,
    drug_name: drugName,
    quantity,
    days_supply: daysSupply,
    instructions,
    health_plan: healthPlan,
}) => ({
    rxNumber,
    patientName: formatNameFirstLast(patientFirstName, patientLastName),
    drugName,
    quantity,
    daysSupply,
    instructions,
    healthPlan,
});

const mapRemainingTransferScript = ({
    rx_number: rxNumber,
    patient_first_name: patientFirstName,
    patient_last_name: patientLastName,
    drug_name: drugName,
    quantity,
    days_supply: daysSupply,
    instructions,
    health_plan: healthPlan,
    pharmacy_npi: pharmacyNpi,
    pharmacy_name: pharmacyName,
    ineligibility_reason: ineligibilityReason,
    source,
}) => ({
    rxNumber,
    patientName: formatNameLastFirst(patientLastName, patientFirstName),
    drugName,
    quantity,
    daysSupply,
    instructions,
    healthPlan,
    pharmacyNpi,
    pharmacyName,
    ineligibilityReason,
    source,
});

const mapAdditionalFile = ({
    file_id: fileId,
    pharmacy_name: pharmacy,
    prescription_count: prescriptions,
    ineligibility_reason: ineligibilityReason,
    source_counts: sourceCounts,
}) => ({
    id: fileId,
    pharmacy,
    prescriptions,
    ineligibilityReason,
    sourceCounts: sourceCounts?.map(({ source, script_count: scriptCount }) => ({
        source,
        scriptCount,
    })),
});

const mapRemainingTransfersBreakdownRow = ({ exception, prescriptions }) => ({
    exception,
    prescriptions,
});

const mapRemainingTransfersBreakdown = ({
    totals,
    pharmacies,
    treatments_not_found: treatmentsNotFound,
}) => ({
    totals: totals?.map(({ label, value }) => ({ label, value })) ?? [],
    pharmacies:
        pharmacies?.map(({ pharmacy_name: pharmacyName, rows }) => ({
            pharmacyName,
            rows: rows?.map(mapRemainingTransfersBreakdownRow) ?? [],
        })) ?? [],
    treatmentsNotFound: {
        sources:
            treatmentsNotFound?.sources?.map(({ source, prescriptions }) => ({
                source,
                prescriptions,
            })) ?? [],
    },
});

const mapProcessingFile = ({
    file_id: fileId,
    pharmacy_name: pharmacy,
    payer_name: payer,
    script_count: scriptCount,
    prescription_count: prescriptionCount,
    file_name: fileName,
}) => ({
    id: fileId,
    pharmacy,
    payer,
    prescriptions: scriptCount ?? prescriptionCount,
    fileName,
});

const mapSentFile = ({
    file_id: fileId,
    pharmacy_name: pharmacy,
    payer_name: payer,
    script_count: scriptCount,
    sending_user_upn: sendingUserUpn,
    file_name: fileName,
    sent_date: sentDate,
}) => ({
    id: fileId,
    receivingPharmacy: pharmacy,
    payer,
    prescriptions: scriptCount,
    sentByUpn: sendingUserUpn,
    fileName,
    sentDate,
});

const mapInboundFileListItem = ({
    inbound_ortf_id: inboundOrtfId,
    file_name: fileName,
    file_path: filePath,
    sending_pharmacy_name: sendingPharmacyName,
    date_received: dateReceived,
    script_count: scriptCount,
    processing_completed_at: processingCompletedAt,
}) => ({
    inboundOrtfId,
    fileName,
    filePath,
    sendingPharmacyName,
    dateReceived,
    scriptCount,
    processingCompletedAt,
});

const mapLabeledCountItems = (items) =>
    items?.map(({ label, count, payer_id: payerId }) => ({
        label: label ?? payerId,
        count,
    })) ?? [];

const mapBreakdownGroup = (group) => ({
    totalCount: group?.total_count ?? 0,
    items: mapLabeledCountItems(group?.items),
});

const mapInboundFileOverview = ({
    inbound_ortf_id: inboundOrtfId,
    file_name: fileName,
    file_path: filePath,
    date_received: dateReceived,
    total_rx_count: totalRxCount,
    outbound_files: outboundFiles,
    remaining_transfers: remainingTransfers,
    manual_transfers: manualTransfers,
}) => ({
    inboundOrtfId,
    fileName,
    filePath,
    dateReceived,
    totalRxCount,
    outboundFiles: mapBreakdownGroup(outboundFiles),
    remainingTransfers: mapBreakdownGroup(remainingTransfers),
    manualTransfers: mapBreakdownGroup(manualTransfers),
});

const TagType = {
    OUTBOUND_FILES: 'OutboundFiles',
    PROCESSING_FILES: 'ProcessingFiles',
    REMAINING_TRANSFERS: 'RemainingTransfers',
};

const invalidateOrtfGenerationTags = (_, error) =>
    !error
        ? [TagType.OUTBOUND_FILES, TagType.REMAINING_TRANSFERS, TagType.PROCESSING_FILES]
        : undefined;

export const ndpApi = createApi({
    baseQuery: getBaseQuery(NDP_API_URL),
    refetchOnMountOrArgChange: true,
    reducerPath: 'ndpApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        generateOutboundFiles: build.mutation({
            query: ({ fileIds = [] } = {}) => ({
                url: '/ortf',
                method: 'POST',
                body: { file_ids: fileIds },
            }),
            invalidatesTags: invalidateOrtfGenerationTags,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred generating outbound files.',
            ),
        }),
        planOutboundFiles: build.mutation({
            query: () => ({
                url: '/ortf/outbound-files/plan',
                method: 'POST',
            }),
            invalidatesTags: invalidateOrtfGenerationTags,
            transformResponse: (response) => ({
                files: response.files.map(mapOutboundPlanGroup),
                remainingTransfersId: response.remaining_transfers_id,
            }),
        }),
        getOutboundFiles: build.query({
            query: () => ({ url: '/ortf' }),
            providesTags: [TagType.OUTBOUND_FILES],
            transformResponse: (response) => response.map(mapOutboundFile),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving outbound files.',
            ),
        }),
        getOutboundFileScripts: build.query({
            query: ({ outboundFileId }) => ({
                url: '/ortf/outbound-files/scripts',
                params: {
                    outbound_file_id: outboundFileId,
                },
            }),
            transformResponse: (response) => response.scripts.map(mapOutboundFileScript),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving outbound file scripts.',
            ),
        }),
        getSentOutboundFileScripts: build.query({
            query: ({ outboundFileId }) => ({
                url: `/ortf/outbound-files/${encodeURIComponent(outboundFileId)}/scripts`,
            }),
            transformResponse: (response) => response.scripts.map(mapOutboundFileScript),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving outbound file scripts.',
            ),
        }),
        getRemainingTransfers: build.query({
            query: () => ({ url: '/ortf/remaining-transfers' }),
            providesTags: [TagType.REMAINING_TRANSFERS],
            transformResponse: (response) => response?.map(mapAdditionalFile),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving remaining transfers.',
            ),
        }),
        getRemainingTransfersV2: build.query({
            query: () => ({ url: '/ortf/remaining-transfers/realtime' }),
            providesTags: [TagType.REMAINING_TRANSFERS],
            transformResponse: (response) => mapRemainingTransfersBreakdown(response),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving remaining transfers.',
            ),
        }),
        getRemainingTransfersBatch: build.query({
            query: (remainingTransfersId) => ({
                url: `/ortf/remaining-transfers/${encodeURIComponent(remainingTransfersId)}`,
            }),
            providesTags: [TagType.REMAINING_TRANSFERS],
            transformResponse: (response) => mapRemainingTransfersBreakdown(response),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving remaining transfers.',
            ),
        }),
        getLatestRemainingTransfersBatch: build.query({
            query: () => ({ url: '/ortf/latest-remaining-transfers-batch' }),
            providesTags: [TagType.REMAINING_TRANSFERS],
            transformResponse: (response) =>
                response?.remaining_transfers_id
                    ? {
                          remainingTransfersId: response.remaining_transfers_id,
                          breakdown: mapRemainingTransfersBreakdown(response),
                      }
                    : undefined,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving the latest remaining transfers batch.',
            ),
        }),
        getRemainingTransferScripts: build.query({
            query: ({ remainingTransfersId }) => ({
                url: '/ortf/remaining-transfers/scripts',
                params: {
                    remaining_transfers_id: remainingTransfersId,
                },
            }),
            providesTags: [TagType.REMAINING_TRANSFERS],
            transformResponse: (response) => ({
                scripts: response.scripts.map(mapRemainingTransferScript),
                count: response.count,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving remaining transfer scripts.',
            ),
        }),
        sendOutboundFiles: build.mutation({
            query: ({ fileIds }) => ({
                url: '/ortf/outbound-files/send',
                method: 'POST',
                body: { file_ids: fileIds },
            }),
            invalidatesTags: (_, error) =>
                !error ? [TagType.OUTBOUND_FILES, TagType.PROCESSING_FILES] : undefined,
        }),
        sendOutboundFilesV2: build.mutation({
            query: ({ outboundFileIds }) => ({
                url: '/ortf/outbound-files/send/v2',
                method: 'POST',
                body: { outbound_file_ids: outboundFileIds },
            }),
            invalidatesTags: (_, error) =>
                !error ? [TagType.OUTBOUND_FILES, TagType.PROCESSING_FILES] : undefined,
        }),
        excludeOutboundFileScript: build.mutation({
            query: ({ rxNumber, outboundFileId }) => ({
                url: `/ortf/outbound-files/scripts/${encodeURIComponent(rxNumber)}/outbound-file`,
                method: 'DELETE',
                params: {
                    outbound_file_id: outboundFileId,
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred removing the prescription from the outbound file.',
            ),
        }),
        getProcessingFiles: build.query({
            query: () => ({ url: '/ortf/files/processing' }),
            providesTags: [TagType.PROCESSING_FILES],
            transformResponse: (response) => response?.map(mapProcessingFile),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving processing files.',
            ),
        }),
        getSentFiles: build.query({
            query: () => ({ url: '/ortf', params: { status: 'sent' } }),
            transformResponse: (response) => response?.map(mapSentFile),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving sent files.'),
        }),
        getInboundFiles: build.query({
            query: () => ({ url: '/ortf/inbound-files' }),
            transformResponse: (response) => response?.map(mapInboundFileListItem),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving inbound files.',
            ),
        }),
        getCompletedInboundFiles: build.query({
            query: () => ({
                url: '/ortf/inbound-files',
                params: { processing_complete: true, limit: 200 },
            }),
            transformResponse: (response) => response?.map(mapInboundFileListItem),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving completed inbound files.',
            ),
        }),
        getInboundFileOverview: build.query({
            query: (inboundOrtfId) => ({
                url: `/ortf/inbound-files/${encodeURIComponent(inboundOrtfId)}`,
            }),
            transformResponse: mapInboundFileOverview,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving inbound file overview.',
            ),
        }),
        downloadOrtfFile: build.mutation({
            query: ({ outboundFileId }) => ({
                url: `/ortf/files/v2/${encodeURIComponent(outboundFileId)}`,
                responseHandler: (response) => response.blob(),
            }),
        }),
    }),
});

export const {
    useGenerateOutboundFilesMutation,
    usePlanOutboundFilesMutation,
    useGetOutboundFilesQuery,
    useGetOutboundFileScriptsQuery,
    useGetSentOutboundFileScriptsQuery,
    useGetRemainingTransfersQuery,
    useGetRemainingTransfersV2Query,
    useLazyGetRemainingTransfersV2Query,
    useLazyGetRemainingTransfersBatchQuery,
    useGetLatestRemainingTransfersBatchQuery,
    useGetRemainingTransferScriptsQuery,
    useSendOutboundFilesMutation,
    useSendOutboundFilesV2Mutation,
    useExcludeOutboundFileScriptMutation,
    useGetProcessingFilesQuery,
    useGetSentFilesQuery,
    useGetInboundFilesQuery,
    useGetCompletedInboundFilesQuery,
    useGetInboundFileOverviewQuery,
    useDownloadOrtfFileMutation,
} = ndpApi;
